import { NgZone, inject, isDevMode } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Store } from "@ngrx/store";
import { take } from "rxjs/operators";
import { createChooseSpecificationsForm } from "src/app/components/views/choose-specifications/choose-specification-form.helper";
import { createExtraServicesForm } from "src/app/components/views/extra-services/extra-services-form.helper";
import {
  IWindscreenWizardForm,
  IVboBookingForm
} from "src/app/interfaces/interfaces";
import { PackagesFilterPipe } from "src/app/pipes/packages-filter.pipe";
import { PackagesServiceFilterPipe } from "src/app/pipes/packages-service-filter.pipe";
import { FormService } from "src/app/services/form.service";
import { PagingService } from "src/app/services/paging.service";
import { PackageTypeEnum } from "src/domain/client";
import { BusinessProposalService } from "./services/business-proposal.service";
import { BookingState, hydrateBookingState, selectBookingState } from "./state";

type ISerializedFormService = {
  form: Omit<RawFormValue, "chooseSpecificationsForm" | "extraServicesForm"> &
    Partial<
      Pick<RawFormValue, "chooseSpecificationsForm" | "extraServicesForm">
    >;
  windscreenWizardForm: ReturnType<
    FormGroup<IWindscreenWizardForm>["getRawValue"]
  >;
  bookingState: Partial<BookingState>;
  legacyFormServiceState: {
    previousFormValue: any;
    isStateDialogOpen: boolean;
  };
};

type RawFormValue = ReturnType<FormGroup<IVboBookingForm>["getRawValue"]>;

declare global {
  var _devHelpers: DevHelpers | undefined;
}

const AUTOLOAD = Symbol("AUTOLOAD");

/**
 * DevHelpers - Development utility for saving and restoring application state
 *
 * This class provides testing utilities that work with the NgRx store to save
 * and restore the complete application state, including:
 * - NgRx BookingState (all API responses, selections, etc.)
 * - Form values from all steps
 * - Current page location
 *
 * Usage (available in browser console when in development mode):
 *
 * 1. Save current state:
 *    saveFormState()              // Saves to "DEFAULT"
 *    saveFormState("my-scenario") // Saves with custom name
 *    saveFormState("AUTOLOAD")    // Auto-restores on page refresh
 *
 * 2. Load saved state:
 *    loadFormState()              // Loads "DEFAULT"
 *    loadFormState("my-scenario") // Loads custom named state
 *
 * 3. Clear auto-load:
 *    sessionStorage.removeItem('VBO_DEV_STORED_STATE_AUTOLOAD')
 *
 * Benefits of NgRx integration:
 * - Single source of truth for all application state
 * - Type-safe state serialization/deserialization
 * - Consistent with production state management
 * - Easy to test complex multi-step scenarios
 */
export class DevHelpers {
  private formService = inject(FormService);
  private pagingService = inject(PagingService);
  private packagesServiceFilterPipe = inject(PackagesServiceFilterPipe);
  private packagesFilterPipe = inject(PackagesFilterPipe);
  private zone = inject(NgZone);
  private businessProposalService = inject(BusinessProposalService);
  private store = inject(Store);

  constructor() {
    this.loadFormState(AUTOLOAD);
    Object.assign(window, {
      loadFormState: this.loadFormState.bind(this),
      saveFormState: this.saveFormState.bind(this)
    });
  }

  loadFormState(maybeName?: string | typeof AUTOLOAD) {
    const isAutoload = maybeName === AUTOLOAD;
    const name = isAutoload ? "AUTOLOAD" : maybeName || "DEFAULT";
    const stored = window.sessionStorage.getItem(
      `VBO_DEV_STORED_STATE_${name}`
    );
    if (stored == null) {
      if (!isAutoload) console.error(`Cannot find stored state \`${name}\``);
      return;
    }
    let serialized: any;
    let formStep: unknown;
    try {
      ({ formStep, ...serialized } = JSON.parse(stored));
    } catch (e) {
      console.error(e);
      if (isAutoload) {
        console.error(
          `Failed to parse json when deserializing stored state\nThe \`${name}\` state was cleared to prevent repeat issues`
        );
        window.sessionStorage.removeItem(`VBO_DEV_STORED_STATE_${name}`);
      } else {
        console.error(
          `Failed to parse json when deserializing stored state \`${name}\``
        );
      }
      return;
    }
    try {
      this.zone.run(() => {
        this.deserializeForm(serialized);
        if (typeof formStep === "number")
          this.pagingService.navigateToStep(formStep);
      });
    } catch (e) {
      console.error(e);
      const errMsg = `Failed to deserialize \`${name}\`\nThe webpage is currently in an undefined state. A reload is recommended.`;
      if (isAutoload) {
        window.sessionStorage.removeItem(`VBO_DEV_STORED_STATE_${name}`);
        console.error(
          `${errMsg}\nThe \`${name}\` state was cleared to prevent repeat issues`
        );
      } else {
        console.error(errMsg);
      }
    }
  }

  saveFormState(name?: string) {
    name = name || "DEFAULT";
    const toStore = {
      ...this.serializeForm(),
      formStep: this.pagingService.formStepLocation
    };
    console.log(toStore);
    window.sessionStorage.setItem(
      `VBO_DEV_STORED_STATE_${name}`,
      JSON.stringify(toStore)
    );
  }

  serializeForm(): ISerializedFormService {
    const rawformValue: ISerializedFormService["form"] =
      this.formService.form.getRawValue();
    if (this.pagingService.formStepLocation < 4) {
      delete rawformValue.chooseSpecificationsForm;
    }
    if (this.pagingService.formStepLocation < 5) {
      delete rawformValue.extraServicesForm;
    }

    // Get the current NgRx state
    let currentBookingState: BookingState;
    this.store
      .select(selectBookingState)
      .pipe(take(1))
      .subscribe((state) => {
        currentBookingState = state;
      });

    // Serialize the booking state for storage
    const serializedBookingState: Partial<BookingState> = {
      chassiNumber: currentBookingState.chassiNumber,
      odometer: currentBookingState.odometer,
      searchedByRegno: currentBookingState.searchedByRegno,
      providerIds: currentBookingState.providerIds,
      carInfo: currentBookingState.carInfo
        ? (currentBookingState.carInfo.toJSON() as any)
        : null,
      techUpdateStatus: currentBookingState.techUpdateStatus
        ? (currentBookingState.techUpdateStatus.toJSON() as any)
        : null,
      activeWorkshopSystems: currentBookingState.activeWorkshopSystems?.map(
        (r) => r.toJSON() as any
      ),
      selectedWorkshopSystems: currentBookingState.selectedWorkshopSystems,
      dealers: currentBookingState.dealers?.map((r) => r.toJSON() as any),
      selectedDealerNumber: currentBookingState.selectedDealerNumber,
      enabledServiceTypes: currentBookingState.enabledServiceTypes,
      basePackages: currentBookingState.basePackages?.map(
        (r) => r.toJSON() as any
      ),
      insuranceProviders: currentBookingState.insuranceProviders?.map(
        (r) => r.toJSON() as any
      ),
      warningLights: currentBookingState.warningLights?.map(
        (r) => r.toJSON() as any
      ),
      vehicleSegment: currentBookingState.vehicleSegment,
      externalTyreUrl: currentBookingState.externalTyreUrl
        ? (currentBookingState.externalTyreUrl.toJSON() as any)
        : null,
      selectedPackages: currentBookingState.selectedPackages,
      rejectedIsOnlyTyre: currentBookingState.rejectedIsOnlyTyre,
      fileIds: currentBookingState.fileIds,
      additionalPackages: currentBookingState.additionalPackages?.map(
        (r) => r.toJSON() as any
      ),
      availableTimes: currentBookingState.availableTimes?.map(
        (r) => r.toJSON() as any
      ),
      selectedDate: currentBookingState.selectedDate,
      selectedTime: currentBookingState.selectedTime,
      selectedDropoffOptionId: currentBookingState.selectedDropoffOptionId,
      packagesUsedForTimeRequest:
        currentBookingState.packagesUsedForTimeRequest,
      contactInformation: currentBookingState.contactInformation,
      campaignResponse: currentBookingState.campaignResponse
        ? (currentBookingState.campaignResponse.toJSON() as any)
        : null,
      campaignValidationState: currentBookingState.campaignValidationState,
      orderId: currentBookingState.orderId,
      bookingError: currentBookingState.bookingError,
      isBookingMadeSuccessful: currentBookingState.isBookingMadeSuccessful,
      proposalCustomer: currentBookingState.proposalCustomer,
      isBusinessProposalActive: currentBookingState.isBusinessProposalActive
    };

    return {
      form: rawformValue,
      windscreenWizardForm: this.formService.windscreenWizardForm.getRawValue(),
      bookingState: serializedBookingState,
      legacyFormServiceState: {
        previousFormValue: this.formService.previousFormValue,
        isStateDialogOpen: this.formService.isStateDialogOpen
      }
    };
  }

  deserializeForm({
    form,
    windscreenWizardForm,
    bookingState,
    legacyFormServiceState
  }: ISerializedFormService) {
    // Dispatch the hydrate action to restore NgRx state
    this.store.dispatch(hydrateBookingState({ state: bookingState }));

    // Restore legacy form service state
    if (legacyFormServiceState) {
      this.formService.previousFormValue =
        legacyFormServiceState.previousFormValue;
      this.formService.isStateDialogOpen =
        legacyFormServiceState.isStateDialogOpen;
    }

    // Wait for state to be hydrated before continuing
    // Use a setTimeout to allow the store to update
    setTimeout(() => {
      const {
        extraServicesForm,
        chooseSpecificationsForm,
        timeSelectionForm,
        ...basicForm
      } = form;

      // Set basic form values
      Object.entries(basicForm).forEach(([control, val]) => {
        try {
          this.formService.form.controls[control].setValue(val);
        } catch (err) {
          try {
            err.message = `${err.message} (${control})`;
          } catch {}
          throw err;
        }
      });

      // Recreate dynamic forms based on restored state
      if (chooseSpecificationsForm) {
        this.store
          .select(selectBookingState)
          .pipe(take(1))
          .subscribe((state) => {
            const basePackages = state.basePackages || [];
            const additionalPackages = state.additionalPackages || [];
            const serviceTypeSelection = state.selectedWorkshopSystems || [];
            const warningLights = state.warningLights || [];

            const newForm = createChooseSpecificationsForm(
              basePackages,
              additionalPackages,
              serviceTypeSelection,
              warningLights,
              this.businessProposalService,
              this.packagesServiceFilterPipe,
              this.packagesFilterPipe
            );
            newForm.setValue(chooseSpecificationsForm);
            this.formService.form.removeControl("chooseSpecificationsForm");
            this.formService.form.addControl(
              "chooseSpecificationsForm",
              newForm
            );
          });
      }

      if (extraServicesForm) {
        this.store
          .select(selectBookingState)
          .pipe(take(1))
          .subscribe((state) => {
            const packages = state.additionalPackages || [];
            const newForm = createExtraServicesForm(packages);
            newForm.setValue(extraServicesForm);
            this.formService.form.removeControl("extraServicesForm");
            this.formService.form.addControl("extraServicesForm", newForm);
          });
      }

      if (bookingState.availableTimes) {
        this.store
          .select(selectBookingState)
          .pipe(take(1))
          .subscribe((state) => {
            const basePackages = state.basePackages || [];
            this.formService.initSpecificFormFromPackages(
              this.formService.timeSelectionForm,
              basePackages.filter(
                (p) => p.type == PackageTypeEnum.DropoffOption
              )
            );
          });
      }

      // Convert date strings back to Date objects for timeSelectionForm
      if (timeSelectionForm) {
        const timeFormWithDate = {
          ...timeSelectionForm,
          // Convert date string to Date object if it exists and is a string
          date:
            timeSelectionForm.date && typeof timeSelectionForm.date === "string"
              ? new Date(timeSelectionForm.date)
              : timeSelectionForm.date
        };
        this.formService.form.controls.timeSelectionForm.setValue(
          timeFormWithDate
        );
      }

      this.formService.windscreenWizardForm.setValue(windscreenWizardForm);
    }, 100);
  }
}

/**
 * Factory function to instantiate DevHelpers in development mode only.
 * Use with `provideAppInitializer(() => provideDevHelpers())` in app.config.ts
 */
export function provideDevHelpers() {
  if (!isDevMode()) return;
  globalThis._devHelpers = new DevHelpers();
}
